import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users } from 'lucide-react';

export interface SensorDisplayStyle01Props {
  type: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  unit?: string;
  label?: string;
  size?: 'small' | 'medium' | 'large';
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  unit: string;
  label: string;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-500',
    unit: '°C',
    label: '溫度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-500',
    unit: '%',
    label: '濕度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-500',
    unit: 'W',
    label: '功率',
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? `${(v / 1000).toFixed(2)} kW` : `${v.toFixed(0)} W`;
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-400',
    unit: 'V',
    label: '電壓',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-400',
    unit: 'A',
    label: '電流',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    unit: 'kWh',
    label: '累積用電',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-500',
    unit: 'lux',
    label: '光照',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-500',
    unit: '',
    label: '佔用狀態',
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 01 - Card
 * Centered card with icon and large value display
 */
export const Style01_Card: React.FC<SensorDisplayStyle01Props> = ({
  type,
  value,
  unit,
  label,
  size = 'medium'
}) => {
  const config = SENSOR_CONFIG[type];
  if (!config) return null;

  const displayUnit = unit ?? config.unit;
  const displayLabel = label ?? config.label;
  const displayValue = config.formatValue(value);
  const Icon = config.icon;

  const sizeClasses = {
    small: { value: 'text-lg', icon: 'w-4 h-4', padding: 'p-3' },
    medium: { value: 'text-3xl', icon: 'w-6 h-6', padding: 'p-4' },
    large: { value: 'text-5xl', icon: 'w-8 h-8', padding: 'p-6' }
  };

  const sizes = sizeClasses[size];
  const valueWithUnit = type === 'power' ? displayValue : `${displayValue}${displayUnit}`;

  return (
    <div className={`bg-slate-800/30 rounded-xl ${sizes.padding} text-center`}>
      <div className="flex justify-center mb-2">
        <Icon className={`${sizes.icon} ${config.color}`} />
      </div>
      <p className="text-xs text-slate-400 mb-1">{displayLabel}</p>
      <p className={`${sizes.value} font-bold ${config.color}`}>
        {valueWithUnit}
      </p>
    </div>
  );
};

export default Style01_Card;
